import crypto from 'crypto';
import { toArray } from 'lodash';
import MobileDetect from 'mobile-detect';
import { appStoreUrl, homePageUrl, playStoreUrl } from '../config/const';
import moment from 'moment';
import { formatDateGlobal } from '../constants/globalConstants';
import { NumericString } from './utilTypes';

/**
 * Add 0-padding to number
 * Ex: (5, 2) => 05, (15, 2) => 15
 * @param num number
 * @param size length of digits
 */
export const padNumber = (num: number, size: number): string => {
  let s = num + '';

  if (num < 0) {
    return s;
  }

  while (s.length < size) s = '0' + s;
  return s;
};

/**
 * Convert seconds to format 'hh : mm : ss'
 * @param timeSecond seconds
 */
export const convertTime = (timeSecond: number): string => {
  if (timeSecond <= 0) {
    return '00 : 00 : 00';
  }

  const hours = Math.floor(timeSecond / 3600);
  const hoursString = padNumber(hours, 2);

  const minutes = Math.floor((timeSecond % 3600) / 60);
  const minutesString = padNumber(minutes, 2);

  const seconds = timeSecond % 60;
  const secondsString = padNumber(seconds, 2);

  const timeString = `${hoursString} : ${minutesString} : ${secondsString}`;
  return timeString;
};

/**
 * Read image from url and return ArrayBuffer
 * @param url url of image
 * @returns Promise that contain buffer data
 */
export const loadImageBuffer = (url: string): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.responseType = 'arraybuffer';
    xhr.onload = function () {
      if (this.status == 200) {
        resolve(this.response);
      } else {
        reject(this);
      }
    };
    xhr.send();
  });
};

/**
 * Return array of digital from number
 * @param number input number
 * @returns array of digital
 */
export const numberToDigital = (number: number | undefined): number[] => {
  if (number === undefined) {
    return [];
  }
  return number.toString(10).replace(/\D/g, '0').split('').map(Number);
};

/**
 * Get random unique uuid with length of 32
 * @returns unique uuid string
 */
export const randomUUID = (): string => {
  return crypto.randomBytes(32).toString('hex');
};

/**
 * Get level background color base on current level
 * @param level level of user
 * @returns color hex code for background color
 */
export const getLevelColor = (level: number | undefined): string => {
  if (level === undefined) {
    return '#0acff3';
  }
  if (level < 19) {
    return '#0acff3';
  } else if (level >= 19 && level < 29) {
    return '#09c2e4';
  } else if (level >= 29 && level < 39) {
    return '#48DCF7';
  } else if (level >= 39 && level < 49) {
    return '#09b6d5';
  } else if (level >= 49 && level < 59) {
    return '#ff30d4';
  } else if (level >= 59 && level < 69) {
    return '#e439c9';
  } else if (level >= 69 && level < 79) {
    return '#FF83E5';
  } else if (level >= 79 && level < 89) {
    return '#ff0fcc';
  } else if (level >= 89 && level < 99) {
    return '#f800c3';
  } else if (level >= 99 && level < 109) {
    return '#2cc5cb';
  } else if (level >= 109 && level < 129) {
    return '#28aeb3';
  } else if (level >= 129 && level < 149) {
    return '#23989d';
  } else if (level >= 149 && level < 169) {
    return '#168a8f';
  } else if (level >= 169 && level < 199) {
    return '#00858a';
  } else if (level >= 199 && level < 219) {
    return '#9843d6';
  } else if (level >= 219 && level < 249) {
    return '#8e34d2';
  } else if (level >= 249 && level < 299) {
    return '#852cc8';
  } else if (level >= 300) {
    return '#7b28b8';
  }

  return '#0acff3';
};

/**
 * Format date string
 * @param date date string
 * @returns formated date
 */
export const formatDate = (date: string): string => {
  const time = Date.parse(date);
  const formatDate = new Date(time);

  return formatDate.toLocaleDateString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const isToday = (date: string): boolean => {
  return moment(date).isSame(Date.now(), 'day');
};

/**
 * Get remain time (countdown) from ms
 * @param endTime end time in ms duration
 * @returns string of duration countdown
 */
export const getRemainTime = (
  endTime: number,
  isShowDetail: boolean,
  isShowFullHour = false,
): string => {
  const total = endTime;
  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  let secondString = seconds.toString();
  if (seconds < 10) {
    secondString = `0${secondString}`;
  }

  let minuteString = minutes.toString();
  if (minutes < 10) {
    minuteString = `0${minuteString}`;
  }

  let hourString = hours.toString();
  if (hours < 10) {
    hourString = `0${hourString}`;
  }

  if (!isShowDetail) {
    if (days >= 1) {
      return `${days}日 ${hourString}:${minuteString}`;
    }
    if (hours >= 1 || isShowFullHour) {
      return `${hourString}:${minuteString}:${secondString}`;
    } else {
      return `${minuteString}:${secondString}`;
    }
  } else {
    if (days >= 1) {
      return `${days}日 ${hourString}時 ${minuteString}分 ${seconds}秒`;
    } else {
      return `${hourString}時 ${minuteString}分 ${seconds}秒`;
    }
  }
};

export const fromNowToEndDate = (
  date: string | undefined,
  showFullHour = false,
  currentDate?: number,
): string => {
  if (date === undefined) {
    return '';
  }
  if (currentDate === undefined) {
    currentDate = Date.now();
  }
  const endDate = Date.parse(date);

  const range = endDate - currentDate;

  return getRemainTime(range, false, showFullHour);
};

export const formatDateByMoment = (
  date: string | Date,
  type: string,
): string => {
  return moment(date).format(type);
};

export const formatDateForPost = (date: string): string | undefined => {
  const currentDate = new Date();
  const postDate = new Date(date);
  const range = currentDate.getTime() - postDate.getTime();

  const minutes = Math.floor((range / 1000 / 60) % 60);
  const hours = Math.floor((range / (1000 * 60 * 60)) % 24);
  const days = Math.floor(range / (1000 * 60 * 60 * 24));
  if (days === 0 && hours === 0) {
    return `${minutes} 分前`;
  }
  if (days === 0 && hours > 0) {
    return `${hours} 時間前`;
  }
  if (days > 0) {
    return formatDateByMoment(postDate, formatDateGlobal.dateFormat);
  }
};

/**
 * Validate age number is in [12, 80]
 * @param age age number
 * @returns is valid?
 */
export const validateAge = (age: number): boolean => {
  return !isNaN(age) && age >= 12 && age <= 80;
};

/**
 * Validate email
 * @param email email string
 * @returns is valid?
 */
export const validateEmail = (email: string): boolean => {
  const regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  return regex.test(email);
};

/**
 * Check login ID is valid. Has length in 4 to 14
 * @param loginId login ID string
 * @returns is valid?
 */
export const validateLoginId = (loginId: string): boolean => {
  // convert to array to detect non-word char like emoji, \n
  const arrChar = toArray(loginId);
  return arrChar.length >= 4 && arrChar.length <= 14;
};

/**
 * Format big number with 1000 seperated commas
 * @param number number to format
 * @returns formatted string: Example: 123456 -> 123,456
 */
export const formatBigNumber = (number: number): string => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Handle when accessing from mobile browser
 * @returns: true if current is mobile browser, false otherwise
 */
export const checkMobileBrowser = (): boolean => {
  const md = new MobileDetect(window.navigator.userAgent);
  if (md.os() === 'AndroidOS') {
    window.location.href = playStoreUrl;
    return true;
  } else if (md.os() === 'iOS') {
    window.location.href = appStoreUrl;
    return true;
  } else if (md.mobile() !== null) {
    window.location.href = homePageUrl;
    return true;
  }
  return false;
};
// convert string to url

export const ReplaceURLs = (message: string | undefined): string => {
  if (!message) return '';

  const urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g;

  return message.replace(urlRegex, function (url): string {
    let withProtocol = url;

    if (!withProtocol.startsWith('http')) {
      withProtocol = 'http://' + url;
    }

    const newStr = `<a
  class="text-link"
  href="${withProtocol}"
  target="_blank"
  rel="noreferrer"
>
  ${url}
</a>`;

    return newStr;
  });
};

/**
 * Generate a random UUID with 12 characters
 * @returns UUIDCode
 */
export const generateUUID = (): string => {
  const randomUUID = self.crypto.randomUUID();
  const codeArr = randomUUID.split('-');
  return codeArr[codeArr.length - 1];
};

export const formatEndLiveMatch = (
  date: string | undefined,
  value: number | undefined,
): string => {
  if (!date || value === undefined) {
    return '';
  }
  const endDate = new Date(date);
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = value % 60;

  endDate.setHours(endDate.getHours() + hours);
  endDate.setMinutes(endDate.getMinutes() + minutes);
  endDate.setSeconds(endDate.getSeconds() + seconds);
  return endDate.toString();
};

export const convertDataToUID = (
  value: string | undefined,
): number | undefined => {
  if (!value) {
    return;
  }
  const hash = parseInt(
    value
      .replace(/[^0-9 ]/g, '')
      .replace(/[0]/g, '1')
      .slice(0, 9),
  );
  return hash;
};
